import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useRecoilState } from 'recoil';
import { FaGlobe, FaTag, FaCoins, FaTriangleExclamation, FaRocket, FaEye, FaTv, FaBullseye, FaSatelliteDish, FaShieldHalved, FaCircleCheck, FaBolt } from 'react-icons/fa6';
import SelectTile from '../primitives/SelectTile.jsx';
import Segmented from '../primitives/Segmented.jsx';
import RowToggle from '../primitives/RowToggle.jsx';
import { Field } from '../primitives/bits.jsx';
import autoDubService from '../../../services/autoDub/autoDubService.js';
import { userState } from '../../../store.jsx';
import { matchVideos, aggregateCost } from '../../../services/autoDub/cost.js';

const inputCls =
	'w-full bg-white border-[1.5px] border-slate-200 rounded-xl px-3.5 py-2.5 text-sm text-slate-800 outline-none focus:border-primary focus:ring-[3px] focus:ring-primary/10 transition placeholder:text-slate-400';

/* Step 4 — publishing rules, monthly cap and the cost recap (PRD §9). */
export default function StepGoLive({ state, set }) {
	const p = state.publish;
	const setP = (patch) => set({ publish: { ...p, ...patch } });

	const { data: videos = [] } = useQuery({
		queryKey: ['autoDubVideos', state.source?.id],
		queryFn: () => autoDubService.listVideos(state.source?.id),
		enabled: !!state.source,
	});

	// Live balance — same source the app header shows (users/me → available_credit).
	const [authMe] = useRecoilState(userState);
	const balance = Number(authMe?.available_credit ?? 0);

	const matched = useMemo(() => matchVideos(videos, state.filters), [videos, state.filters]);
	const nLang = Math.max(1, state.langs.length);
	const { perLang, total } = aggregateCost(matched, nLang);
	const perUpload = Math.round(total / Math.max(1, matched.length));
	const estimate = state.type === 'backfill' ? total : perUpload;
	const insufficient = estimate > balance;

	return (
		<div>
			{/* scoped keyframes for the hero's rocket + sparkle motif */}
			<style>{`@keyframes adRise{0%,100%{transform:translateY(2px)}50%{transform:translateY(-5px)}}@keyframes adTwinkle{0%,100%{opacity:.25;transform:scale(.7)}50%{opacity:1;transform:scale(1.15)}}`}</style>

			{/* gradient hero */}
			<div className="relative overflow-hidden rounded-3xl mb-5 px-6 py-7 md:px-9 md:py-9" style={{ background: 'linear-gradient(120deg,#2a0a4f 0%,#5A0AE0 52%,#8C2CE2 100%)' }}>
				<div className="pointer-events-none absolute -top-16 -right-10 w-56 h-56 rounded-full bg-white/10 blur-2xl" />
				<div className="pointer-events-none absolute -bottom-24 left-1/4 w-56 h-56 rounded-full bg-fuchsia-400/20 blur-3xl" />

				{/* rocket + sparkles */}
				<div className="pointer-events-none absolute top-1/2 -translate-y-1/2 right-9 hidden sm:block w-24 h-20">
					<span className="absolute right-3 top-1/2 -translate-y-1/2 w-14 h-14 rounded-2xl bg-white grid place-items-center text-primary shadow-lg" style={{ animation: 'adRise 2.4s ease-in-out infinite' }}><FaRocket size={20} /></span>
					<span className="absolute left-1 top-2 text-white text-[13px]" style={{ animation: 'adTwinkle 1.8s ease-in-out infinite' }}>✦</span>
					<span className="absolute left-6 bottom-1 text-white text-[16px]" style={{ animation: 'adTwinkle 1.8s ease-in-out infinite', animationDelay: '.5s' }}>✦</span>
					<span className="absolute left-0 bottom-7 text-white text-[10px]" style={{ animation: 'adTwinkle 1.8s ease-in-out infinite', animationDelay: '.9s' }}>✦</span>
				</div>

				<div className="relative">
					<span className="inline-flex items-center gap-2 rounded-full bg-white/15 backdrop-blur px-3 py-1.5 text-[11px] font-bold uppercase tracking-wide text-white">
						<FaRocket size={12} /> Step 4 · Go live
					</span>
					<h2 className="mt-4 text-[26px] md:text-[30px] font-bold text-white tracking-tight leading-tight">Last step — how dubs go public</h2>
					<p className="mt-2 text-[14.5px] text-white/80 leading-relaxed max-w-xl">Set the publishing rules once. You can change any of this later from the workflow page.</p>
				</div>
			</div>

			{/* setup recap */}
			<div className="bg-white rounded-3xl border border-slate-200 shadow-small px-5 py-4 mb-6 flex items-center gap-5 flex-wrap">
				<div className="flex items-center gap-2.5">
					<div className="w-9 h-9 rounded-xl bg-primary/10 text-primary grid place-items-center flex-none"><FaTv size={15} /></div>
					<div className="min-w-0">
						<div className="text-[11px] text-slate-400 leading-none">Source channel</div>
						<div className="text-[13.5px] font-bold text-slate-700 mt-1 truncate max-w-[170px]">{state.source?.name || '—'}</div>
					</div>
				</div>
				<div className="w-px h-9 bg-slate-200" />
				<div className="flex items-center gap-2.5">
					<div className="w-9 h-9 rounded-xl bg-primary/10 text-primary grid place-items-center flex-none"><FaBullseye size={15} /></div>
					<div>
						<div className="text-[11px] text-slate-400 leading-none">Scope</div>
						<div className="text-[13.5px] font-bold text-slate-700 mt-1">{state.type === 'backfill' ? 'Existing videos' : 'Future uploads'}</div>
					</div>
				</div>
				<div className="w-px h-9 bg-slate-200" />
				<div className="flex items-center gap-2.5">
					<div className="w-9 h-9 rounded-xl bg-primary/10 text-primary grid place-items-center flex-none"><FaGlobe size={15} /></div>
					<div>
						<div className="text-[11px] text-slate-400 leading-none">Languages</div>
						<div className="text-[13.5px] font-bold text-slate-700 mt-1">{state.langs.length}</div>
					</div>
				</div>
				<div className="w-px h-9 bg-slate-200" />
				<div className="flex items-center gap-2.5">
					<div className="w-9 h-9 rounded-xl bg-primary/10 text-primary grid place-items-center flex-none"><FaSatelliteDish size={15} /></div>
					<div>
						<div className="text-[11px] text-slate-400 leading-none">Publishing</div>
						<div className="text-[13.5px] font-bold text-slate-700 mt-1">{state.publishMode === 'audio_track' ? 'Audio track' : 'New videos'}</div>
					</div>
				</div>
			</div>

			{/* review gate */}
			<div className="flex items-center gap-2.5 mb-3">
				<span className="w-7 h-7 rounded-lg bg-primary/10 text-primary grid place-items-center"><FaShieldHalved size={14} /></span>
				<div className="text-[15px] font-semibold text-slate-700">Before a dub goes public…</div>
			</div>
			<div className="flex gap-3.5 flex-wrap mb-6">
				<SelectTile
					selected={state.review === true} onClick={() => set({ review: true })}
					emoji={<FaCircleCheck size={20} color="#f6a609" />} iconColor="#f6a609" badge="Safest"
					title="I'll review it first"
					desc="Each dub waits in your review queue. Nothing goes live until you hit approve."
				/>
				<SelectTile
					selected={state.review === false} onClick={() => set({ review: false })}
					emoji={<FaBolt size={20} color="#FF0033" />} iconColor="#FF0033"
					title="Publish it for me"
					desc="Fully hands-free — dubs go live the moment they're ready."
				/>
			</div>

			<div className="mb-6">
				<Field label="Name this workflow" hint="So you can spot it in your list later.">
					<input className={inputCls} value={state.name} placeholder="e.g. My channel → Hindi & Spanish" onChange={e => set({ name: e.target.value })} />
				</Field>
			</div>

			<div className="bg-white rounded-3xl border border-slate-200 shadow-small p-1 mb-6">
				<RowToggle icon={<FaGlobe size={17} />} title="Auto-translate title & description"
					desc="Each dub gets a title and description written in its own language."
					on={p.translateMeta} onChange={v => setP({ translateMeta: v })} />
				<div className="h-px bg-slate-100" />
				<RowToggle icon={<FaTag size={17} />} title="Copy original tags & category"
					desc="Keep the same tags and category as the original video, so search and recommendations carry over."
					on={p.copyTags} onChange={v => setP({ copyTags: v })} />
				<div className="h-px bg-slate-100" />
				<div className="flex items-center gap-3.5 px-3.5 py-3 flex-wrap">
					<div className="w-9 h-9 rounded-xl bg-slate-100 text-slate-500 grid place-items-center flex-none"><FaEye size={16} /></div>
					<div className="flex-1 min-w-[180px]">
						<div className="text-sm font-semibold text-slate-800">Visibility</div>
						<div className="text-[12px] text-slate-500 mt-0.5 leading-snug">How each dub is published — same options as YouTube Studio.</div>
					</div>
					<Segmented
						options={[['public', 'Public'], ['unlisted', 'Unlisted'], ['private', 'Private']]}
						value={p.privacy}
						onChange={v => setP({ privacy: v })}
					/>
				</div>
			</div>

			<div className="bg-white rounded-3xl border border-slate-200 shadow-small overflow-hidden grid grid-cols-2 items-stretch max-[850px]:grid-cols-1">
				<div className="p-5">
					<Field label={<span className="text-[12px] font-bold tracking-[0.06em] uppercase text-slate-500">Monthly credit limit</span>} hint="We pause the workflow once it spends this much in a month — no surprise bills.">
						<div className="flex gap-2 items-center">
							<input type="number" step="10000" className={inputCls} value={state.capMonth} onChange={e => set({ capMonth: +e.target.value })} />
							<span className="text-[13px] text-slate-400 whitespace-nowrap">credits / mo</span>
						</div>
					</Field>
				</div>

				{/* cost recap */}
				<div className="p-5 border-l border-slate-100 max-[850px]:border-l-0 max-[850px]:border-t" style={{ background: 'linear-gradient(135deg, rgba(90,10,224,0.06), rgba(140,44,226,0.06))' }}>
					<div className="flex items-center gap-2 mb-2.5">
							<span className="w-7 h-7 rounded-lg bg-primary/10 text-primary grid place-items-center flex-none"><FaCoins size={13} /></span>
							<div className="text-[12px] font-bold tracking-[0.06em] uppercase text-slate-500">What this will cost</div>
						</div>
					{state.type === 'backfill' ? (
						<>
							<div className="flex items-baseline gap-2">
								<span className="text-[30px] font-bold text-primary">≈ {total.toLocaleString()}</span>
								<span className="text-[13px] text-slate-500 font-semibold">credits total</span>
							</div>
							<div className="text-[12px] text-slate-500 mt-1.5">{matched.length} matching video{matched.length !== 1 ? 's' : ''} × {nLang} language{nLang > 1 ? 's' : ''} ({perLang.toLocaleString()} credits per language).</div>
						</>
					) : (
						<>
							<div className="flex items-baseline gap-2">
								<span className="text-[30px] font-bold text-primary">≈ {perUpload.toLocaleString()}</span>
								<span className="text-[13px] text-slate-500 font-semibold">credits / upload</span>
							</div>
							<div className="text-[12px] text-slate-500 mt-1.5">Based on your recent uploads, across {nLang} language{nLang > 1 ? 's' : ''}. Billed per second of video.</div>
						</>
					)}
					<div className="text-[12px] text-slate-500 mt-2.5 flex items-center gap-1.5">
						<FaCoins size={13} className="text-primary" />You have <b>{balance.toLocaleString()}</b> credits.
					</div>
					{insufficient && (
						<div className="mt-2.5 flex items-start gap-2 text-[12px] text-amber-700 bg-amber-50 rounded-xl px-3 py-2">
							<FaTriangleExclamation size={13} className="mt-px flex-none" />
							<span>This estimate is more than your balance. Dubs will pause when credits run out — <button type="button" className="font-semibold text-primary underline">top up</button> to avoid interruptions.</span>
						</div>
					)}
				</div>
			</div>
		</div>
	);
}
